// Which Slack channels feed this project, and adding or removing one.
// A channel reached through a tenant / landlord scope is listed but managed on the
// Global data page, where the scope lives.
import { useState } from "react";
import { useQueryClient } from "react-query";

import { useOperationWatcher } from "../api/hooks/useAsyncOperation";
import {
  useProjectSlack,
  useSlackChannelPicker,
  useSubscribeProjectSlack,
  useSyncProjectSlackNow,
  useUnsubscribeSlack,
  type SlackStageResponse,
} from "../api/hooks/useSlack";
import { QK } from "../api/queryKeys";
import { channelNotes, pickerPlaceholder, scopeLabel, syncToShow } from "./slackChannelNotes";
import { SlackPickerStatus } from "./SlackPickerStatus";
import styles from "./Slack.module.css";

// What a staged sync is doing, polled from its operation key.
export const SlackSyncProgress = ({ staged }: { staged: SlackStageResponse }) => {
  const qc = useQueryClient();
  const op = useOperationWatcher(staged.operation_key, () => {
    void qc.invalidateQueries([QK.projectSlack]);
    void qc.invalidateQueries([QK.slackStatus]);
    void qc.invalidateQueries([QK.slackScopeSubscriptions]);
  });
  const status = op.data?.status;

  if (!staged.operation_key) {
    return <p className={styles.hint}>{staged.message || "No sync was queued."}</p>;
  }
  const triage = op.data?.triage as { detail?: string } | null | undefined;
  const progress = op.data?.progress;
  const label = (() => {
    if (status === "failed") return `failed: ${op.data?.error ?? "unknown error"}`;
    if (status === "completed") return triage?.detail ? `done - ${triage.detail}` : "done";
    if (status === "cancelled") return "cancelled";
    if (!op.data || status === "pending") return "queued";
    const stage = progress?.label ?? "syncing";
    if (!progress || progress.total === null) return stage;
    return `${stage} (${progress.done} of ${progress.total})`;
  })();
  return (
    <p className={status === "failed" ? styles.error : styles.hint}>Slack sync: {label}</p>
  );
};

export const SlackChannelsCard = ({ projectId }: { projectId: string }) => {
  const list = useProjectSlack(projectId);
  const [picking, setPicking] = useState(false);
  const [staged, setStaged] = useState<SlackStageResponse | null>(null);
  const { status, refresh } = useSlackChannelPicker(picking);
  const subscribe = useSubscribeProjectSlack(projectId);
  const unsubscribe = useUnsubscribeSlack();
  const syncNow = useSyncProjectSlackNow(projectId);
  const [choice, setChoice] = useState("");

  const subs = list.data?.subscriptions ?? [];
  const shown = syncToShow(staged, list.data?.active_operation_key);
  const direct = subs.filter((s) => s.project_id !== null);
  const scoped = subs.filter((s) => s.project_id === null);
  const have = new Set(subs.map((s) => s.channel.channel_id));
  const candidates = (status.data?.channels ?? []).filter(
    (c) => c.bot_is_member && !have.has(c.channel_id),
  );
  const chosen = candidates.find((c) => c.channel_id === choice) ?? null;
  const busy = subscribe.isLoading || unsubscribe.isLoading || syncNow.isLoading;

  if (list.data && !list.data.configured && !list.data.workspace) {
    return (
      <section className={styles.card}>
        <h3 className={styles.title}>Slack channels</h3>
        <p className={styles.description}>
          The Slack app is not installed in this environment, so no channel can be
          added yet.
        </p>
      </section>
    );
  }

  const add = () => {
    if (!chosen) return;
    if (
      chosen.is_private &&
      !window.confirm(
        `#${chosen.name} is a private channel. Everyone who can open this project, and its agents, will be able to read it. Add it?`,
      )
    ) {
      return;
    }
    subscribe.mutate(chosen.channel_id, {
      onSuccess: (resp) => {
        setChoice("");
        setPicking(false);
        setStaged(resp);
      },
    });
  };

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={styles.title}>Slack channels</h3>
        <span className={styles.count}>{subs.length} subscribed</span>
      </header>

      {list.isLoading && <p className={styles.description}>Loading channels...</p>}
      {list.isError && <p className={styles.error}>Could not load this project&apos;s channels.</p>}
      {!list.isLoading && subs.length === 0 && (
        <p className={styles.description}>No Slack channel feeds this project yet.</p>
      )}
      {subs.length > 0 && (
        <ul className={styles.list}>
          {direct.map((sub) => (
            <li key={sub.subscription_id} className={styles.row}>
              <div>
                <span className={styles.name}>#{sub.channel.name}</span>
                {channelNotes(sub.channel).length > 0 && (
                  <span className={styles.notes}> - {channelNotes(sub.channel).join(", ")}</span>
                )}
                {sub.project_count > 1 && (
                  <span className={styles.notes}> - also feeds {sub.project_count - 1} other project(s)</span>
                )}
              </div>
              <button
                type="button"
                className={styles.remove}
                aria-label={`Remove #${sub.channel.name}`}
                disabled={busy}
                onClick={() => unsubscribe.mutate(sub.subscription_id)}
              >
                Remove
              </button>
            </li>
          ))}
          {scoped.map((sub) => (
            <li key={sub.subscription_id} className={styles.row}>
              <div>
                <span className={styles.name}>#{sub.channel.name}</span>
                <span className={styles.notes}>
                  {" "}
                  - every project with {scopeLabel(sub)} (managed on the Global data page)
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className={styles.description}>
        Agents read each channel&apos;s new messages as they arrive, and can search its
        history. A channel shared by several projects is read by each of them, so its
        agents are told which messages name this site.
      </p>

      {picking ? (
        <div className={styles.actions}>
          <select
            className={styles.select}
            aria-label="Slack channel to add"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            disabled={status.isLoading}
          >
            <option value="">
              {pickerPlaceholder(candidates.length, status.isLoading || refresh.isLoading)}
            </option>
            {candidates.map((c) => (
              <option key={c.channel_id} value={c.channel_id}>
                #{c.name}
                {c.is_private ? " (private)" : ""}
              </option>
            ))}
          </select>
          <button type="button" className={styles.button} disabled={!chosen || busy} onClick={add}>
            {subscribe.isLoading ? "Adding..." : "Add"}
          </button>
          <button type="button" className={styles.secondary} onClick={() => setPicking(false)}>
            Cancel
          </button>
          <SlackPickerStatus
            refresh={refresh}
            botMention={list.data?.workspace?.bot_mention ?? "the InRange Agents bot"}
            available={candidates.length}
          />
        </div>
      ) : (
        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={() => setPicking(true)}>
            Add a channel
          </button>
          <button
            type="button"
            className={styles.secondary}
            disabled={busy || subs.length === 0}
            onClick={() => syncNow.mutate(undefined, { onSuccess: setStaged })}
          >
            {syncNow.isLoading ? "Queueing..." : "Sync now"}
          </button>
        </div>
      )}

      {subscribe.error && (
        <p className={styles.error}>Could not add the channel. {subscribe.error.message}</p>
      )}
      {unsubscribe.error && (
        <p className={styles.error}>Could not remove the channel. {unsubscribe.error.message}</p>
      )}
      {syncNow.error && <p className={styles.error}>Could not queue a sync. {syncNow.error.message}</p>}
      {shown && <SlackSyncProgress key={shown.operation_key ?? "skip"} staged={shown} />}
    </section>
  );
};
